import { FORM_ERROR } from 'final-form';
import { LocationDescriptorObject } from 'history';
import { Moment } from 'moment';
import { t } from 'ttag';

import { Nullable } from '../../types/general';
import { Comment } from '../../types/user';

import { formatFullName } from '../../lib/helpers';

import { QUERY_DATE_FORMAT } from '../../lib/constants';
import { addDate, formatDate } from '../../lib/date';
import { getLocationQuery, stringifyLocationQuery } from '../../lib/url';
import {
    DocumentFolder,
    DocumentsFilteredByDeliveredState,
} from '../../services/enum';
import { Fields } from '../exportDataPopup/exportDataPopup';
import {
    REVIEW_FOLDERS,
    SIGN_FILTER_COMPATIBLE_FOLDERS,
    SIGN_FOLDERS_QUERY_MAP,
} from './constants';

export function filterByDate(
    dateFrom: Nullable<Moment>,
    dateTo: Nullable<Moment>,
    push: (location: LocationDescriptorObject) => void,
) {
    // TODO: Browser API location usage is discouraged, consider obtainig location of react-router
    const query = getLocationQuery(document.location);

    if (dateFrom) {
        query.date_from = formatDate(dateFrom, QUERY_DATE_FORMAT);
    } else {
        delete query.date_from;
    }

    if (dateTo) {
        query.date_to = formatDate(dateTo, QUERY_DATE_FORMAT);
    } else {
        delete query.date_to;
    }

    push({ pathname: '/app/documents', search: stringifyLocationQuery(query) });
}

interface Amounts {
    amountGte: string;
    amountLte: string;
}

export function filterByDocsAmount(
    { amountGte, amountLte }: Amounts,
    push: (location: LocationDescriptorObject) => void,
) {
    const query = getLocationQuery(document.location);
    if (amountGte) {
        query.amount_gte = amountGte;
    } else {
        delete query.amount_gte;
    }
    if (amountLte) {
        query.amount_lte = amountLte;
    } else {
        delete query.amount_lte;
    }
    push({ pathname: '/app/documents', search: stringifyLocationQuery(query) });
}

export function filterNotDelivered(
    filteredNotDelivered = false,
    push: (location: LocationDescriptorObject) => void,
) {
    let query = { ...getLocationQuery(document.location) };
    if (filteredNotDelivered) {
        delete query.has_date_delivered;
    } else {
        query = {
            ...getLocationQuery(document.location),
            has_date_delivered:
                DocumentsFilteredByDeliveredState.IS_NOT_DELIVERED.value,
        };
        if (query.page) delete query.page;
    }

    push({ pathname: '/app/documents', search: stringifyLocationQuery(query) });
}

export function filterWithoutTags(
    filteredWithoutTags = false,
    push: (location: LocationDescriptorObject) => void,
) {
    // TODO: Browser API location usage is discouraged, consider obtainig location of react-router
    let query = getLocationQuery(document.location);
    if (filteredWithoutTags) {
        delete query.without_tags;
    } else {
        query = {
            ...getLocationQuery(document.location),
            without_tags: 'true',
        };
        if (query.page) delete query.page;
        if (query.tag) delete query.tag;
        if (query.tags) delete query.tags; // ???
    }

    push({ pathname: '/app/documents', search: stringifyLocationQuery(query) });
}

export function getCurrentSignFolder(query: Record<string, unknown>) {
    // check all folders except first
    for (const folderName of (Object.keys(SIGN_FOLDERS_QUERY_MAP) as Array<
        keyof typeof SIGN_FOLDERS_QUERY_MAP
    >).slice(1)) {
        const folderQuery = SIGN_FOLDERS_QUERY_MAP[folderName];
        const matchQuery = (Object.keys(folderQuery) as Array<
            keyof typeof folderQuery
        >).every((key) => {
            return String(query[key]) === String(folderQuery[key]);
        });

        if (matchQuery) return folderName;
    }

    return null;
}

export function filterBySignFolder(
    push: (location: LocationDescriptorObject) => void,
    folder?: keyof typeof SIGN_FOLDERS_QUERY_MAP,
) {
    if (folder) {
        // TODO: Browser API location usage is discouraged, consider obtainig location of react-router
        const currentQuery = getLocationQuery(document.location);
        const folderQuery = SIGN_FOLDERS_QUERY_MAP[folder];
        const folderId = SIGN_FILTER_COMPATIBLE_FOLDERS.includes(
            String(currentQuery.folder_id),
        )
            ? currentQuery.folder_id
            : undefined;

        push({
            pathname: '/app/documents',
            search: stringifyLocationQuery({
                ...currentQuery,
                ...folderQuery,
                folder_id: folderId,
                page: undefined,
            }),
        });
    }
}

export function filterByDocumentCategory(
    push: (location: LocationDescriptorObject) => void,
    location: LocationDescriptorObject,
    categories?: string | string[],
) {
    const query = getLocationQuery(location);

    if (categories) {
        delete query.page;
        query.categories = categories;
    }
    push({
        pathname: '/app/documents',
        search: stringifyLocationQuery(query),
    });
}

export function filterByReviewFolder(
    push: (location: LocationDescriptorObject) => void,
    folder?: string,
) {
    // TODO: Browser API location usage is discouraged, consider obtainig location of react-router
    const searchParams = new URLSearchParams(document.location.search);

    if (folder && folder !== REVIEW_FOLDERS[0].value) {
        searchParams.set('folder_id', DocumentFolder.NOT_INTERNAL.value);
        searchParams.append('folder_id', DocumentFolder.INTERNAL.value);
        searchParams.set('review_folder', folder);

        if (searchParams.has('page')) {
            searchParams.delete('page');
        }
    } else {
        searchParams.delete('review_folder');
        searchParams.set('folder_id', DocumentFolder.NOT_INTERNAL.value);
    }

    push({ pathname: '/app/documents', search: searchParams.toString() });
}

export function formatCommentsForExport(comments: Array<Comment>) {
    if (comments.length === 0) return null;
    return comments
        .slice(Math.max(comments.length - 5, 0))
        .map(
            (comment) =>
                `${formatFullName(comment.role.user)}: ${comment.text}`,
        )
        .join(', ');
}

export const validateDocumentsExport = (fields: Fields) => {
    if (!fields.fromDate || !fields.toDate || !fields.format) {
        return {
            fromDate: t`Будь ласка, заповніть всі поля`,
        };
    } else if (new Date(fields.fromDate) > new Date(fields.toDate)) {
        return {
            [FORM_ERROR]: t`Початок періоду не може бути пізнишим ніж кінець`,
        };
    } else if (addDate(fields.fromDate, 1, 'year') < new Date(fields.toDate)) {
        return {
            [FORM_ERROR]: t`Максимальний період вивантаження складає 1 рік, вкажіть менший період.`,
        };
    }

    return {};
};
